import { VALTYPES } from '../../../../data/interfaces';
import { categoricalOrderOptions } from '../../helpers/categoricalSorters';
import {
  createConfigParamCountPicker,
  createConfigParamLabelSpace,
  createConfigParamMultipleChoice,
  createConfigParamTypeRange,
} from '../../helpers/configParams';
import { TpChartManifest } from '../interfaces';
import { ContingencyTable } from './ContingencyTable';

export const manifestContingencyTable: TpChartManifest = {
  id: 'ContingencyTable',
  name: 'Contingency table',
  description: `Visualizes the **relationship** between **two categorical properties**.
The states of the first and second property are used to define the rows and columns of the plot.
For each cell, a disc is visualized with a size proportional to the number of occurrences of that combination of states.
`,
  component: ContingencyTable,
  canvasBased: true,
  groups: [
    { groupId: 'x', groupName: 'Horizontal' },
    { groupId: 'y', groupName: 'Vertical' },
    { groupId: 'appearance', groupName: 'Appearance' },
  ],
  aspects: [
    {
      id: 'catsY',
      name: 'Vertical categories',
      description: 'States of this property define the rows of the contingency table',
      groupId: 'y',
      required: true,
      quality: 1,
      dataType: [VALTYPES.CATEGORICAL, VALTYPES.MULTICAT],
    },
    {
      id: 'catsX',
      name: 'Horizontal categories',
      description: 'States of this property define the columns of the contingency table',
      groupId: 'x',
      required: true,
      quality: 1,
      dataType: [VALTYPES.CATEGORICAL, VALTYPES.MULTICAT],
    },
  ],
  configParams: [
    {
      id: 'sortByX',
      name: 'Sort by',
      groupId: 'x',
      description: 'How to sort the categories along the X axis.',
      paramType: createConfigParamMultipleChoice(categoricalOrderOptions),
    },
    {
      id: 'maxCatCountX',
      name: 'Max number of categories',
      groupId: 'x',
      description:
        'In case the number of states exceeds this value, the remaining ones will be grouped into a single column, and represented as hollow discs.',
      paramType: createConfigParamCountPicker([10, 20, 50, 100, 150, 200, 300], 50),
    },
    {
      id: 'labelSpaceX',
      name: 'Label space',
      groupId: 'x',
      description:
        'Scaling factor that determines how much space in the chart is used by the names of the different column states.',
      paramType: createConfigParamLabelSpace(),
    },
    {
      id: 'sortByY',
      name: 'Sort by',
      groupId: 'y',
      description: 'How to sort the categories along the Y axis.',
      paramType: createConfigParamMultipleChoice(categoricalOrderOptions),
    },
    {
      id: 'maxCatCountY',
      name: 'Max number of categories',
      description:
        'In case the number of states exceeds this value, the remaining ones will be grouped into a single row, and represented as hollow discs.',
      groupId: 'y',
      paramType: createConfigParamCountPicker([10, 20, 50, 100, 150, 200, 300], 50),
    },
    {
      id: 'labelSpaceY',
      name: 'Label space',
      description:
        'Scaling factor that determines how much space in the chart is used by the names of the different row states.',
      groupId: 'y',
      paramType: createConfigParamLabelSpace(),
    },
    {
      id: 'gamma',
      name: 'Size gamma correction',
      description: `Scaling factor that defines a nonlinear relation between the number of occurrences of a cell and the displayed disc size. 
This can be used to emphasise cells with few occurrences.`,
      groupId: 'appearance',
      paramType: createConfigParamTypeRange(0, 10, 0.1, 5),
    },
  ],

  examples: [
    {
      id: 'contingency_1',
    },
  ],
};
